import type { Department } from '@opti-sea/core';
import { z } from 'zod';

/**
 * Content layers (PLAN §2.11): base RISQ content → company layer (Admin, DPA) → vessel layer
 * (Admin, DPA, Master). A lower layer overrides the one above; base content never changes.
 * Database enums `risq_layer`, `risq_layer_target`, `risq_layer_action`.
 */
export const RISQ_LAYERS = ['company', 'vessel'] as const;
export type RisqLayer = (typeof RISQ_LAYERS)[number];
export const risqLayerSchema = z.enum(RISQ_LAYERS);

export const RISQ_LAYER_TARGETS = ['positions', 'wtc_item', 'flags'] as const;
export type RisqLayerTarget = (typeof RISQ_LAYER_TARGETS)[number];

/** `set` change · `add` own checklist point · `revert` back to the layer above · `remove` own point. */
export const RISQ_LAYER_ACTIONS = ['set', 'add', 'revert', 'remove'] as const;
export type RisqLayerAction = (typeof RISQ_LAYER_ACTIONS)[number];
export const risqLayerActionSchema = z.enum(RISQ_LAYER_ACTIONS);

/** Where an effective value comes from. */
export type RisqLayerSource = 'base' | RisqLayer;

export interface ResolvedPositions {
  codes: readonly string[];
  source: RisqLayerSource;
}

/**
 * Responsible positions of a question: vessel layer, else company layer, else the default
 * distribution from the master file. Same rule as `private.risq_question_positions_for`.
 */
export function resolvePositions(layers: {
  base: readonly string[];
  company?: readonly string[] | null;
  vessel?: readonly string[] | null;
}): ResolvedPositions {
  if (layers.vessel) {
    return { codes: layers.vessel, source: 'vessel' };
  }
  if (layers.company) {
    return { codes: layers.company, source: 'company' };
  }
  return { codes: layers.base, source: 'base' };
}

/**
 * Work scope on the phone (display only — the server decides, PLAN §2.6, D38): the Master works
 * on everything; an officer on questions of own position; the C/E also on every question given to
 * an engine-department position.
 */
export function isInWorkScope(
  position: string,
  codes: readonly string[],
  departmentOf: (code: string) => Department | undefined,
): boolean {
  if (position === 'MASTER' || codes.includes(position)) {
    return true;
  }
  return position === 'CE' && codes.some((code) => departmentOf(code) === 'engine');
}

/** Who may change a layer (same rule as `private.risq_can_change_layer`). */
export function canChangeLayer(
  layer: RisqLayer,
  roles: { admin?: boolean; dpa?: boolean; master?: boolean },
): boolean {
  if (roles.admin === true || roles.dpa === true) {
    return true;
  }
  return layer === 'vessel' && roles.master === true;
}

/** A base checklist point of a question ("What to check"). */
export interface BaseChecklistPoint {
  id: string;
  itemKey: string;
  sortOrder: number;
  textEn: string;
}

/** A row of the company checklist layer: change of a base point, or an own point. */
export interface CompanyChecklistPoint {
  id: string;
  itemKey: string;
  isOwn: boolean;
  /** Base point: null keeps the base text. Own point: the text. */
  textEn: string | null;
  hidden: boolean;
  sortOrder: number;
}

export interface ChecklistPoint {
  /** `base`: tick refers to `risq_wtc_items.id`; `own`: to `risq_company_wtc_items.id`. */
  kind: 'base' | 'own';
  id: string;
  itemKey: string;
  text: string;
  /** Text changed by the company (base point) or the company's own point. */
  fromCompany: boolean;
}

/**
 * The checklist a vessel sees: base points in order with company changes applied, hidden points
 * left out, then the company's own points. Same rule as the dashboard editor (Task 9).
 */
export function resolveChecklist(
  base: readonly BaseChecklistPoint[],
  company: readonly CompanyChecklistPoint[],
): ChecklistPoint[] {
  const changes = new Map(company.filter((c) => !c.isOwn).map((c) => [c.itemKey, c]));
  const points: ChecklistPoint[] = [];
  for (const point of [...base].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const change = changes.get(point.itemKey);
    if (change?.hidden === true) {
      continue;
    }
    points.push({
      kind: 'base',
      id: point.id,
      itemKey: point.itemKey,
      text: change?.textEn ?? point.textEn,
      fromCompany: change?.textEn != null,
    });
  }
  const own = company
    .filter((c) => c.isOwn && c.textEn !== null)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  for (const point of own) {
    points.push({
      kind: 'own',
      id: point.id,
      itemKey: point.itemKey,
      text: point.textEn ?? '',
      fromCompany: true,
    });
  }
  return points;
}
